import { useEffect, useState, useRef, useCallback } from 'react';

import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
dayjs.extend(utc);

/**
 * 券面画面でのカウントダウンを実装
 */
const useCountDown = ({
    limit,
    timeOverCallback = () => {},
}: {
    limit?: Date | null;
    timeOverCallback?: () => void;
}) => {
    // state
    const [until, setUntil] = useState<number | null>(null);
    const intervalRef = useRef<NodeJS.Timeout>();
    const [countDownText, setCountDownText] = useState('');

    /**
     * 呼ばれる毎に、残り時間を減らす処理
     */
    const updateTimer = useCallback(() => {
        if (!limit) {
            setUntil(null);
            return;
        }
        const nowutc = dayjs().utc();
        const diff = dayjs(limit).diff(nowutc, 'second');
        if (isNaN(diff)) {
            setUntil(null);
            return;
        }
        const nextDiff = diff - 1;
        setUntil(nextDiff);
        // 小なりイコールだと表示直後にエラーになるので、または条件で記載する（厳密な違いによるもの）
        if (nextDiff === 0 || nextDiff < 0) {
            timeOverCallback();
            clearInterval(intervalRef.current);
        }
    }, [limit, timeOverCallback]);

    /**
     * 1秒毎に時刻を取得
     */
    useEffect(() => {
        updateTimer();
        intervalRef.current = setInterval(() => {
            updateTimer();
        }, 1000);
        return () => {
            clearInterval(intervalRef.current);
        };
    }, [updateTimer]);

    /**
     * あと何日何時間何分何秒かを返す
     * @returns h:m:s
     */
    const getTimeLeft = useCallback((until: number | null) => {
        if (until === null) {
            return {
                seconds: '--',
                minutes: '--',
                hours: '--',
                totalHours: '--',
                days: '--',
            };
        } else if (until < 0) {
            return {
                seconds: '00',
                minutes: '00',
                hours: '00',
                totalHours: '00',
                days: '00',
            };
        } else {
            return {
                seconds: (until % 60).toString().padStart(2, '0'),
                minutes: (Math.floor(until / 60) % 60).toString().padStart(2, '0'),
                hours: (Math.floor(until / (60 * 60)) % 24).toString().padStart(2, '0'),
                totalHours: Math.floor(until / (60 * 60))
                    .toString()
                    .padStart(2, '0'),
                days: Math.floor(until / (60 * 60 * 24))
                    .toString()
                    .padStart(2, '0'),
            };
        }
    }, []);

    useEffect(() => {
        const { totalHours, minutes, seconds } = getTimeLeft(until);
        setCountDownText(`${totalHours}:${minutes}:${seconds}`);
    }, [until, getTimeLeft]);

    return countDownText;
};

export default useCountDown;
